// Deterministic zero-order-hold frame-rate converter.
//
// Maps an arbitrary (possibly variable) source frame timeline onto a strict, monotonically
// increasing output grid at a fixed output frame rate. Each output slot reuses whichever
// source frame was most recently presented at that slot's start time, so source frames are
// duplicated (upsampling, e.g. 24fps -> 30fps) or dropped (downsampling, e.g. 60fps -> 30fps)
// as needed. Output timestamps are derived from real source timestamps (never an assumed
// constant source fps), never repeat, and never go backwards, so drift never accumulates
// beyond the true difference between source and output timing.

export interface ScheduledOutputFrame {
    // Index of the source sample whose decoded image should be drawn/re-drawn for this output frame.
    sourceIndex: number
    timestampUs: number
    durationUs: number
}

export class FrameScheduler {
    private readonly frameDurationUs: number
    private nextOutputUs = 0
    private pendingSourceIndex: number | null = null

    constructor(outFps: number) {
        if (!(outFps > 0)) {
            throw new Error(`FrameScheduler: outFps must be positive, got ${outFps}`)
        }
        this.frameDurationUs = Math.round(1_000_000 / outFps)
    }

    // Call once per decoded source frame, in increasing timestamp order.
    // Returns the output frames (if any) that became finalized because this new source frame
    // supersedes the previous one.
    push(sourceIndex: number, timestampSec: number): ScheduledOutputFrame[] {
        const tsUs = Math.round(timestampSec * 1_000_000)
        const emitted = this.emitUpTo(tsUs)
        this.pendingSourceIndex = sourceIndex
        return emitted
    }

    // Call once after the last source frame, passing the (exclusive) end of the decode
    // window/stream, to flush any trailing output slots still covered by the last frame.
    finish(endTimestampSec: number): ScheduledOutputFrame[] {
        const endUs = Math.round(endTimestampSec * 1_000_000)
        return this.emitUpTo(endUs)
    }

    private emitUpTo(boundaryUs: number): ScheduledOutputFrame[] {
        const out: ScheduledOutputFrame[] = []
        if (this.pendingSourceIndex === null) return out

        while (this.nextOutputUs < boundaryUs) {
            out.push({
                sourceIndex: this.pendingSourceIndex,
                timestampUs: this.nextOutputUs,
                durationUs: this.frameDurationUs,
            })
            this.nextOutputUs += this.frameDurationUs
        }
        return out
    }
}
